var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { theme } from '../../../config';
import { TableCell, TableRow, sortQueuesByName } from '../../common';
import { ExpandableRowLabel } from '../../common/tableHierarchy';
import { useCurrencySymbol } from '../../hooks/useCurrencyOptions';
import { QueueType } from '../../multiskill/types';
import { useResourcingContext } from '../../report/hooks';
import { DisplayMode } from '../../report/resourcing';
import { useDisplayModeParams } from '../../report/selectors';
import { PLAN_COLORS } from '../../wizard/constants';
import { FirstCell, STICKIES } from '../annualTable';
import { getCountryExpansionKey, useControlledExpansion } from '../common/selectors';
import { getChunkBaselineValue, getGroupedMultiskillScenarioCellColor } from '../common/utils';
import { HIERARCHY_LEVEL, MULTISKILL_COLOR, NEST_LEVEL } from '../constants';
import { generateVariances } from '../selectors';
var findScenarioQueue = function (lob, queue) {
  var queues = queue.isNonDef ? lob.nonDefQueues : lob.defQueues;
  return queues.find(function (scenarioQueue) {
    return scenarioQueue.name === queue.name;
  });
};
var findScenarioCountry = function (queue, country) {
  return queue === null || queue === void 0 ? void 0 : queue.countries.find(function (scenarioCountry) {
    return scenarioCountry.countryName === country.countryName;
  });
};
var findScenarioLocation = function (country, location) {
  return country === null || country === void 0 ? void 0 : country.locations.find(function (scenarioLocation) {
    return scenarioLocation.locationName === location.locationName;
  });
};
var isGroupScenario = function (scenario, groupName) {
  var _a;
  return (scenario === null || scenario === void 0 ? void 0 : scenario.dataType) === QueueType.Multiskill && (!groupName || !!((_a = scenario.multiSkill) === null || _a === void 0 ? void 0 : _a.some(function (group) {
    return group.name === groupName;
  })));
};
var findRegularQueue = function (scenario, lobName, queueName, isNonDef) {
  var _a;
  var lob = (_a = scenario === null || scenario === void 0 ? void 0 : scenario.queuesByLob) === null || _a === void 0 ? void 0 : _a.find(function (entry) {
    return entry.lobName === lobName;
  });
  if (isNonDef) {
    var fromLob_1 = lob === null || lob === void 0 ? void 0 : lob.nonDefQueues.find(function (q) {
      return q.name === queueName;
    });
    if (fromLob_1) return fromLob_1;
    return scenario === null || scenario === void 0 ? void 0 : scenario.nonDefQueue.find(function (q) {
      return q.name === queueName;
    });
  }
  var fromLob = lob === null || lob === void 0 ? void 0 : lob.defQueues.find(function (q) {
    return q.name === queueName;
  });
  if (fromLob) return fromLob;
  return scenario === null || scenario === void 0 ? void 0 : scenario.defQueue.find(function (q) {
    return q.name === queueName;
  });
};
var getSupportStaffCellColor = function (scenario, opts) {
  var _a, _b, _c, _d;
  if (opts.isMultiskillGroup) {
    return isGroupScenario(scenario, opts.groupName) ? MULTISKILL_COLOR : (_b = PLAN_COLORS[(_a = scenario === null || scenario === void 0 ? void 0 : scenario.colorIndex) !== null && _a !== void 0 ? _a : -1]) === null || _b === void 0 ? void 0 : _b[1];
  }
  if (!opts.lobName || !opts.queueName) return (_d = PLAN_COLORS[(_c = scenario === null || scenario === void 0 ? void 0 : scenario.colorIndex) !== null && _c !== void 0 ? _c : -1]) === null || _d === void 0 ? void 0 : _d[1];
  var regularQueue = findRegularQueue(scenario, opts.lobName, opts.queueName, !!opts.isNonDef);
  return getGroupedMultiskillScenarioCellColor(scenario, regularQueue === null || regularQueue === void 0 ? void 0 : regularQueue.group, regularQueue === null || regularQueue === void 0 ? void 0 : regularQueue.isMultiskill);
};
var getScenarioValues = function (perScenarioLob, queue, valueKey) {
  var scenarioQueues = perScenarioLob.map(function (scenLob) {
    return findScenarioQueue(scenLob, queue);
  });
  return {
    // Value per scenario for this queue
    scenarioValues: scenarioQueues.map(function (scenarioQueue) {
      var _a;
      return (_a = scenarioQueue === null || scenarioQueue === void 0 ? void 0 : scenarioQueue[valueKey]) !== null && _a !== void 0 ? _a : null;
    }),
    // Value per scenario for each country
    scenarioCountryValues: queue.countries.map(function (country) {
      return scenarioQueues.map(function (scenarioQueue) {
        var _a, _b;
        return (_b = (_a = findScenarioCountry(scenarioQueue, country)) === null || _a === void 0 ? void 0 : _a[valueKey]) !== null && _b !== void 0 ? _b : null;
      });
    }),
    // Value per scenario for each location within each country
    scenarioLocationValues: queue.countries.map(function (country) {
      return country.locations.map(function (location) {
        return scenarioQueues.map(function (scenarioQueue) {
          var _a, _b;
          return (_b = (_a = findScenarioLocation(findScenarioCountry(scenarioQueue, country), location)) === null || _a === void 0 ? void 0 : _a[valueKey]) !== null && _b !== void 0 ? _b : null;
        });
      });
    })
  };
};
export var SupportStaffLocationRow = function (_a) {
  var location = _a.location,
    scenarioValues = _a.scenarioValues,
    data = _a.data,
    chunkSize = _a.chunkSize,
    _b = _a.decimal,
    decimal = _b === void 0 ? 1 : _b,
    currencySymbol = _a.currencySymbol,
    _c = _a.isMultiskillGroup,
    isMultiskillGroup = _c === void 0 ? false : _c,
    groupName = _a.groupName,
    lobName = _a.lobName,
    queueName = _a.queueName,
    _d = _a.isNonDef,
    isNonDef = _d === void 0 ? false : _d;
  var isPercentages = useResourcingContext().isPercentages;
  var displayMode = useDisplayModeParams()[0];
  var _e = useCurrencySymbol(currencySymbol),
    currencyPrefix = _e.prefix,
    currencySuffix = _e.suffix;
  var varianceOptions = {
    decimalPlaces: currencySymbol ? 0 : undefined,
    currencyPrefix: currencyPrefix,
    currencySuffix: currencySuffix
  };
  var multiskillGroupName = isMultiskillGroup ? groupName : undefined;
  return _jsxs(TableRow, {
    children: [_jsx(FirstCell, {
      "aria-label": NEST_LEVEL[HIERARCHY_LEVEL.supportStaffLocation],
      stickySizes: STICKIES,
      value: location.locationName,
      color: isMultiskillGroup ? MULTISKILL_COLOR : theme.palette.background.default
    }), scenarioValues.map(function (value, index, arr) {
      var baseValue = getChunkBaselineValue(scenarioValues, index, chunkSize);
      return _jsx(TableCell, {
        colSpan: 2,
        value: value === null && !isMultiskillGroup ? 0 : value,
        decimal: decimal,
        currency: currencySymbol,
        color: getSupportStaffCellColor(data[index], {
          isMultiskillGroup: isMultiskillGroup,
          groupName: multiskillGroupName,
          lobName: lobName,
          queueName: queueName,
          isNonDef: isNonDef
        }),
        highlightBorder: index + 1 !== arr.length && !((index + 1) % chunkSize),
        expandchildren: displayMode === DisplayMode.All ? undefined : generateVariances(baseValue !== null && baseValue !== void 0 ? baseValue : null, value, isPercentages, varianceOptions)
      }, "loc-".concat(location.locationName, "-").concat(index));
    })]
  });
};
export var SupportStaffCountryRow = function (_a) {
  var country = _a.country,
    scenarioValues = _a.scenarioValues,
    scenarioLocationValues = _a.scenarioLocationValues,
    data = _a.data,
    chunkSize = _a.chunkSize,
    expandedProp = _a.expandedProp,
    onToggle = _a.onToggle,
    _b = _a.decimal,
    decimal = _b === void 0 ? 1 : _b,
    currencySymbol = _a.currencySymbol,
    _c = _a.isMultiskillGroup,
    isMultiskillGroup = _c === void 0 ? false : _c,
    groupName = _a.groupName,
    lobName = _a.lobName,
    queueName = _a.queueName,
    _d = _a.isNonDef,
    isNonDef = _d === void 0 ? false : _d;
  var _e = useControlledExpansion(expandedProp, onToggle),
    expanded = _e.expanded,
    handleToggle = _e.handleToggle;
  var isPercentages = useResourcingContext().isPercentages;
  var displayMode = useDisplayModeParams()[0];
  var _f = useCurrencySymbol(currencySymbol),
    currencyPrefix = _f.prefix,
    currencySuffix = _f.suffix;
  var varianceOptions = {
    decimalPlaces: currencySymbol ? 0 : undefined,
    currencyPrefix: currencyPrefix,
    currencySuffix: currencySuffix
  };
  var hasLocations = !!country.locations.length;
  var multiskillGroupName = isMultiskillGroup ? groupName : undefined;
  return _jsxs(_Fragment, {
    children: [_jsxs(TableRow, {
      children: [_jsx(FirstCell, __assign({
        "aria-label": NEST_LEVEL[HIERARCHY_LEVEL.supportStaffCountry],
        stickySizes: STICKIES,
        color: isMultiskillGroup ? MULTISKILL_COLOR : undefined
      }, {
        children: _jsx(ExpandableRowLabel, __assign({
          expanded: expanded,
          onToggle: hasLocations ? handleToggle : undefined,
          bold: true
        }, {
          children: country.countryName
        }))
      })), scenarioValues.map(function (value, index, arr) {
        var baseValue = getChunkBaselineValue(scenarioValues, index, chunkSize);
        return _jsx(TableCell, {
          colSpan: 2,
          value: value === null && !isMultiskillGroup ? 0 : value,
          decimal: decimal,
          currency: currencySymbol,
          bold: true,
          color: getSupportStaffCellColor(data[index], {
            isMultiskillGroup: isMultiskillGroup,
            groupName: multiskillGroupName,
            lobName: lobName,
            queueName: queueName,
            isNonDef: isNonDef
          }),
          highlightBorder: index + 1 !== arr.length && !((index + 1) % chunkSize),
          expandchildren: displayMode === DisplayMode.All ? undefined : generateVariances(baseValue !== null && baseValue !== void 0 ? baseValue : null, value, isPercentages, varianceOptions)
        }, "country-".concat(country.countryName, "-").concat(index));
      })]
    }), hasLocations && expanded && country.locations.map(function (loc, locIdx) {
      var _a;
      return _jsx(SupportStaffLocationRow, {
        location: loc,
        scenarioValues: (_a = scenarioLocationValues[locIdx]) !== null && _a !== void 0 ? _a : scenarioValues.map(function () {
          return null;
        }),
        data: data,
        chunkSize: chunkSize,
        decimal: decimal,
        currencySymbol: currencySymbol,
        isMultiskillGroup: isMultiskillGroup,
        groupName: groupName,
        lobName: lobName,
        queueName: queueName,
        isNonDef: isNonDef
      }, "".concat(country.countryName, "-").concat(loc.locationName, "-").concat(locIdx));
    })]
  });
};
var SupportStaffQueueRow = function (_a) {
  var queue = _a.queue,
    scenarioValues = _a.scenarioValues,
    scenarioCountryValues = _a.scenarioCountryValues,
    scenarioLocationValues = _a.scenarioLocationValues,
    data = _a.data,
    chunkSize = _a.chunkSize,
    expansionKey = _a.expansionKey,
    expandedProp = _a.expandedProp,
    onToggle = _a.onToggle,
    expandedItems = _a.expandedItems,
    onToggleItem = _a.onToggleItem,
    _b = _a.decimal,
    decimal = _b === void 0 ? 1 : _b,
    currencySymbol = _a.currencySymbol,
    lobName = _a.lobName,
    isNonDef = _a.isNonDef;
  var _c = useControlledExpansion(expandedProp, onToggle),
    expanded = _c.expanded,
    handleToggle = _c.handleToggle;
  var isPercentages = useResourcingContext().isPercentages;
  var displayMode = useDisplayModeParams()[0];
  var _d = useCurrencySymbol(currencySymbol),
    currencyPrefix = _d.prefix,
    currencySuffix = _d.suffix;
  var varianceOptions = {
    decimalPlaces: currencySymbol ? 0 : undefined,
    currencyPrefix: currencyPrefix,
    currencySuffix: currencySuffix
  };
  var hasCountries = !!queue.countries.length;
  return _jsxs(_Fragment, {
    children: [_jsxs(TableRow, {
      children: [_jsx(FirstCell, __assign({
        "aria-label": NEST_LEVEL[HIERARCHY_LEVEL.supportStaffQueue],
        stickySizes: STICKIES
      }, {
        children: _jsx(ExpandableRowLabel, __assign({
          expanded: expanded,
          onToggle: hasCountries ? handleToggle : undefined,
          bold: true
        }, {
          children: queue.name
        }))
      })), scenarioValues.map(function (value, index, arr) {
        var baseValue = getChunkBaselineValue(scenarioValues, index, chunkSize);
        return _jsx(TableCell, {
          colSpan: 2,
          value: value !== null && value !== void 0 ? value : 0,
          decimal: decimal,
          currency: currencySymbol,
          bold: true,
          color: getSupportStaffCellColor(data[index], {
            lobName: lobName,
            queueName: queue.name,
            isNonDef: isNonDef
          }),
          highlightBorder: index + 1 !== arr.length && !((index + 1) % chunkSize),
          expandchildren: displayMode === DisplayMode.All ? undefined : generateVariances(baseValue !== null && baseValue !== void 0 ? baseValue : null, value, isPercentages, varianceOptions)
        }, "queue-".concat(queue.name, "-").concat(index));
      })]
    }), hasCountries && expanded && queue.countries.map(function (country, countryIdx) {
      var _a, _b;
      var countryExpansionKey = expansionKey ? getCountryExpansionKey(expansionKey, country.countryName) : undefined;
      return _jsx(SupportStaffCountryRow, {
        country: country,
        scenarioValues: (_a = scenarioCountryValues[countryIdx]) !== null && _a !== void 0 ? _a : scenarioValues.map(function () {
          return null;
        }),
        scenarioLocationValues: (_b = scenarioLocationValues[countryIdx]) !== null && _b !== void 0 ? _b : [],
        data: data,
        chunkSize: chunkSize,
        expandedProp: countryExpansionKey ? expandedItems === null || expandedItems === void 0 ? void 0 : expandedItems[countryExpansionKey] : undefined,
        onToggle: countryExpansionKey && onToggleItem ? function () {
          return onToggleItem(countryExpansionKey);
        } : undefined,
        decimal: decimal,
        currencySymbol: currencySymbol,
        lobName: lobName,
        queueName: queue.name,
        isNonDef: isNonDef
      }, "".concat(queue.name, "-").concat(country.countryName, "-").concat(countryIdx));
    })]
  });
};
export var SupportStaffLobQueueRows = function (_a) {
  var lob = _a.lob,
    perScenarioLob = _a.perScenarioLob,
    data = _a.data,
    chunkSize = _a.chunkSize,
    expansionKeyPrefix = _a.expansionKeyPrefix,
    expandedItems = _a.expandedItems,
    onToggleItem = _a.onToggleItem,
    _b = _a.valueKey,
    valueKey = _b === void 0 ? 'value' : _b,
    _c = _a.decimal,
    decimal = _c === void 0 ? 1 : _c,
    currencySymbol = _a.currencySymbol;
  // Combine def + non-def queue entries for rendering, sorted alphanumerically
  // so the list doesn't read as all-deferrable-then-all-non-deferrable.
  var allQueues = sortQueuesByName(__spreadArray(__spreadArray([], lob.defQueues.map(function (q) {
    return __assign(__assign({}, q), {
      isNonDef: false
    });
  }), true), lob.nonDefQueues.map(function (q) {
    return __assign(__assign({}, q), {
      isNonDef: true
    });
  }), true));
  return _jsx(_Fragment, {
    children: allQueues.map(function (queue) {
      var _a = getScenarioValues(perScenarioLob, queue, valueKey),
        scenarioValues = _a.scenarioValues,
        scenarioCountryValues = _a.scenarioCountryValues,
        scenarioLocationValues = _a.scenarioLocationValues;
      var queueExpansionKey = "".concat(expansionKeyPrefix, ":queue:").concat(queue.isNonDef ? 'nonDeferrable' : 'deferrable', ":").concat(queue.name);
      return _jsx(SupportStaffQueueRow, {
        queue: queue,
        lobName: lob.lobName,
        isNonDef: queue.isNonDef,
        scenarioValues: scenarioValues,
        scenarioCountryValues: scenarioCountryValues,
        scenarioLocationValues: scenarioLocationValues,
        data: data,
        chunkSize: chunkSize,
        expansionKey: queueExpansionKey,
        expandedProp: expandedItems === null || expandedItems === void 0 ? void 0 : expandedItems[queueExpansionKey],
        onToggle: onToggleItem ? function () {
          return onToggleItem(queueExpansionKey);
        } : undefined,
        expandedItems: expandedItems,
        onToggleItem: onToggleItem,
        decimal: decimal,
        currencySymbol: currencySymbol
      }, queueExpansionKey);
    })
  });
};